import type {
  EvaluationPlanDraft,
  EvaluationPlanWorkspaceDraft,
  EvaluationStandardDraft,
} from '../types/evaluationPlan';
import { loadAdminEvaluationPlanBundleCache } from './adminEvaluationPlanCache';

export type AdminEvaluationPlanWorkspaceSnapshot = {
  standards: EvaluationStandardDraft[];
  plans: EvaluationPlanDraft[];
};

export type EvaluationPlanWorkspaceDraftSources = {
  categoryNames: string[];
  draftsByCategory: Record<string, EvaluationPlanWorkspaceDraft | undefined>;
};

function mergeDraftIntoSnapshot(
  target: {
    standardsByCategory: Map<string, EvaluationStandardDraft[]>;
    plansById: Map<string, EvaluationPlanDraft>;
  },
  categoryName: string,
  draft: EvaluationPlanWorkspaceDraft
): void {
  const name = categoryName.trim();
  if (!name) return;
  target.standardsByCategory.set(name, draft.standards);
  draft.plans.forEach((plan) => {
    target.plansById.set(plan.id, plan);
  });
}

/** sessionStorage·메모리·localStorage 번들 캐시에서 워크스페이스 스냅샷을 조립합니다. */
export function mergeAdminEvaluationPlanWorkspaceSnapshotFromLocal(
  sources: EvaluationPlanWorkspaceDraftSources
): AdminEvaluationPlanWorkspaceSnapshot {
  const standardsByCategory = new Map<string, EvaluationStandardDraft[]>();
  const plansById = new Map<string, EvaluationPlanDraft>();

  for (const rawName of sources.categoryNames) {
    const name = rawName.trim();
    if (!name) continue;

    const draft = sources.draftsByCategory[name];
    if (draft) {
      mergeDraftIntoSnapshot({ standardsByCategory, plansById }, name, draft);
      continue;
    }

    const cached = loadAdminEvaluationPlanBundleCache(name);
    if (cached.found && cached.bundle) {
      standardsByCategory.set(name, cached.bundle.standards);
      cached.bundle.plans.forEach((plan) => {
        plansById.set(plan.id, plan);
      });
    }
  }

  return {
    standards: Array.from(standardsByCategory.values()).flat(),
    plans: Array.from(plansById.values()),
  };
}

/** 로컬 소스에 없어 DB 번들 fetch가 필요한 카테고리 이름 */
export function listMissingAdminEvaluationPlanWorkspaceCategories(
  sources: EvaluationPlanWorkspaceDraftSources
): string[] {
  const missing: string[] = [];
  for (const rawName of sources.categoryNames) {
    const name = rawName.trim();
    if (!name) continue;
    if (sources.draftsByCategory[name]) continue;
    const cached = loadAdminEvaluationPlanBundleCache(name);
    if (cached.found && cached.bundle) continue;
    missing.push(name);
  }
  return missing;
}
